import * as React from 'react';

/**
 * The height that lets a box end where the window does, with everything drawn under it still in
 * view — a list's table, so the page never scrolls beside the table's own scroll (SR-439).
 *
 * **Measured, not laid out.** A list screen is a page header, filters, a strip, the table and a
 * pager in a document that scrolls — some twenty screens, each its own nesting of grids, and
 * Lists' reading pane beside its table. Turning each into a column of the window's height would
 * rewrite every one of them and the shell; a fixed `calc(100dvh - 13rem)` guessed what sits above
 * the table and was wrong wherever a filter bar wraps or a strip is drawn. So the box asks the page:
 * the room from its top to the window's bottom, less what follows it inside the page's `main` — the
 * pager, a legend, the paddings — counted level by level along its ancestors, and only from the
 * siblings drawn *under* it, so a pane beside it is not taken for something below. A sibling
 * marked `[data-fit-skip]` is not counted where it falls under the box either: Lists' reading pane,
 * stacked under the grid on a narrower screen, is reached by scrolling to it, and counted it would
 * take the grid's fit away.
 *
 * **Only where it fits.** Where that room is under `MIN_FIT` — a table midway down a long page
 * (the hub's sections), or a phone whose filters take the window — the answer is `null` and the
 * caller decides: `DataTable` then draws every row and lets the page scroll alone.
 *
 * Measured again when the window resizes and when the document, `main` or any of its children
 * changes size, so a banner drawn above `main`, a filter bar wrapping or a pager arriving moves it.
 * A box outside any `main` — a dialog's — is not fitted. The box's own height never
 * moves the answer (its top and what follows it do not depend on it), so nothing loops.
 */
export function useFitHeight(
  ref: React.RefObject<Nullable<HTMLElement>>,
  enabled = true
): Nullable<number> {
  const [height, setHeight] = React.useState<Nullable<number>>(null);

  React.useLayoutEffect(() => {
    const box = ref.current;
    if (!enabled || !box) {
      setHeight(null);
      return;
    }
    const main = box.closest('main');
    if (!main) {
      setHeight(null);
      return;
    }
    const measure = () => setHeight(fitHeight(box, main));
    measure();
    const observer = new ResizeObserver(measure);
    // The document grows with what is drawn above `main` (the address banner), which `main`'s
    // own boxes, already as small as their content, would not report.
    observer.observe(document.documentElement);
    observer.observe(main);
    for (const child of main.children) observer.observe(child);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref, enabled]);

  return height;
}

/** Below this, a box is not fitted: a table showing two rows is worse than a page that scrolls. */
const MIN_FIT = 160;

function fitHeight(box: HTMLElement, end: HTMLElement): Nullable<number> {
  const top = box.getBoundingClientRect().top + window.scrollY;
  const room = Math.floor(document.documentElement.clientHeight - top - contentBelow(box, end));
  return room >= MIN_FIT ? room : null;
}

/** What is drawn under `box` up to the bottom of `end`: at each level, the siblings that start at
 *  or below the box's bottom — never one beside it — and the parent's bottom padding and border. */
function contentBelow(box: HTMLElement, end: HTMLElement): number {
  let below = 0;
  let child: HTMLElement = box;
  while (child !== end) {
    const parent = child.parentElement;
    if (!parent) break;
    const bottom = child.getBoundingClientRect().bottom;
    let lowest = bottom;
    for (let sibling = child.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
      const rect = sibling.getBoundingClientRect();
      if (rect.height === 0 || rect.top < bottom - 1 || sibling.hasAttribute('data-fit-skip')) {
        continue;
      }
      lowest = Math.max(lowest, rect.bottom + px(getComputedStyle(sibling).marginBottom));
    }
    const style = getComputedStyle(parent);
    below += lowest - bottom + px(style.paddingBottom) + px(style.borderBottomWidth);
    child = parent;
  }
  return below;
}

/** A computed length in pixels, `0` for one the browser leaves blank. */
function px(value: string): number {
  return parseFloat(value) || 0;
}
